import React, { useMemo, useState } from 'react';
import { fmtDate, fmtMoney, useFetch, usePagedSimple } from '../../hooks';
import { Alert, Card, Chip, Segmented, Skeleton } from '../../ui/basics';
import { DataTable } from '../../ui/DataTable';
import { LineChart } from '../../ui/charts';
import { teamName } from '../../components/PlacementsTable';

export const yearChoices = () => {
  const current = new Date().getFullYear();
  return Array.from({ length: 6 }, (_, i) => String(current - i));
};

export default function Weekly({ org, lead }) {
  const years = useMemo(yearChoices, []);
  const [selected, setSelected] = useState([years[0]]);
  const [metric, setMetric] = useState('margin');
  const [point, setPoint] = useState(null); // { year, week }
  const { data, loading, error } = useFetch(selected.length ? 'reports/weekly/' : null,
    { organization: org, lead, years: [...selected].sort().join(',') });
  const list = usePagedSimple(point ? 'reports/weekly/candidates/' : null, { organization: org, lead, year: point && point.year, week: point && point.week });

  const series = useMemo(() => (data ? Object.entries(data.data).map(([year, weeks]) => ({
    id: year,
    data: weeks.map((w) => {
      const value = metric === 'margin' ? w.data[0].margin : w.data[0].active_candidate;
      return { x: w.week.replace('week', 'Week '), y: value === 0 ? null : value };
    }),
  })) : []), [data, metric]);

  const toggle = (year) => {
    setPoint(null);
    setSelected((prev) => (prev.includes(year) ? prev.filter((y) => y !== year) : [...prev, year]));
  };
  const columns = [
    { key: 'candidate_name', label: 'Candidate', render: (r) => <b>{r.candidate_name}</b> },
    { key: 'date_of_joining', label: 'Start', render: (r) => fmtDate(r.date_of_joining) },
    { key: 'project_end', label: 'End', render: (r) => fmtDate(r.project_end) },
    { key: 'client', label: 'Client' }, { key: 'recruiter', label: 'Recruiter' },
    { key: 'lead', label: 'Team', render: (r) => teamName(r.lead) },
    { key: 'gross_margin', label: 'Margin', align: 'right', render: (r) => fmtMoney(r.gross_margin) },
  ];

  return (
    <div className="stack">
      <Card title={`Active ${metric === 'margin' ? 'margin' : 'candidates'} by week`} subtitle="Click a point to list the candidates active that week"
        actions={<div className="row">
          <div className="row" style={{ gap: 6 }}>{years.map((y) => <Chip key={y} on={selected.includes(y)} onClick={() => toggle(y)}>{y}</Chip>)}</div>
          <Segmented value={metric} onChange={setMetric} options={[{ value: 'margin', label: 'Margin' }, { value: 'candidates', label: 'Candidates' }]} />
        </div>}>
        {error && <Alert>{error}</Alert>}
        {!selected.length ? <p className="muted">Pick at least one year.</p>
          : loading ? <Skeleton h={320} />
            : <LineChart data={series} yFormat={metric === 'margin' ? (v) => fmtMoney(v) : undefined}
              onPointClick={(p) => setPoint({ year: p.serieId, week: parseInt(String(p.data.x).replace(/\D/g, ''), 10) })} />}
      </Card>
      {point && (
        <Card flush title={`Candidates active in week ${point.week}, ${point.year}`} subtitle={list.data ? list.data.range : ''}>
          <DataTable columns={columns} paged={list} rowKey={(r) => r.s_no} noun="candidates" />
        </Card>
      )}
    </div>
  );
}
